import includes from 'lodash/includes';

/////////////////////////////

function WidgetDataController($scope, $timeout, Charts, Data, LxNotificationService, Translation, User, Utils) {
    'ngInject';

    const vm = this;

    /////////////////////////////
    //                         //
    //    Private attributes   //
    //                         //
    /////////////////////////////

    /**
     * Chart content: header and values.
     *
     * @type {Object}
     */
    let _chartContent = vm.widget.properties.content;

    /**
     * Chart type: pie, table or raw.
     *
     * @type {string}
     */
    let _chartType = vm.widget.properties.type;

    /////////////////////////////
    //                         //
    //    Public attributes    //
    //                         //
    /////////////////////////////

    /**
     * Defines `chart.js` options.
     *
     * @type {Object}
     */
    vm.chartOptions = {
        legend: {
            display: true,
        },
        maintainAspectRatio: false,
    };

    /////////////////////////////

    /**
     * Services and utilities.
     */
    vm.Charts = Charts;
    vm.Data = Data;

    /////////////////////////////
    //                         //
    //    Private functions    //
    //                         //
    /////////////////////////////

    /**
     * Format url with user data injection.
     *
     * @param  {string} widgetUrl The url to format.
     * @return {string} The formatted url with user data injection.
     */
    async function _formatUrl(widgetUrl) {
        const splitUrl = widgetUrl.split(/\?(.+)/);

        let replacedSplittedUrl = splitUrl[1];
        if (angular.isDefinedAndFilled(splitUrl[1]) && includes(splitUrl[1], 'user.')) {
            replacedSplittedUrl = await Utils.getUrlUserInjectionInformation(splitUrl[1], User.getConnected());
        }

        return `${splitUrl[0]}?${replacedSplittedUrl}`;
    }

    /**
     * Init chart.
     *
     * @param {string} dataType Chart type (pie or table).
     */
    function _initChart(dataType) {
        if (dataType === 'pie') {
            if (
                angular.isDefinedAndFilled(_chartContent) &&
                angular.isDefinedAndFilled(_chartContent.headers) &&
                angular.isDefinedAndFilled(_chartContent.values)
            ) {
                vm.chartData = Charts.getPieData(_chartContent.values[0], _chartContent.headers);
            } else {
                LxNotificationService.error(Translation.translate('WIDGET_TYPE_DATA_DATA_ERROR'));
            }
        } else if (dataType === 'table') {
            vm.chartData = {
                headers: _chartContent.headers || [],
                values: _chartContent.values || [],
            };
        }
    }

    /////////////////////////////
    //                         //
    //     Public functions    //
    //                         //
    /////////////////////////////

    /**
     * Get widget class.
     *
     * @return {Array} An array of class names.
     */
    function getWidgetClass() {
        const widgetClass = [];

        vm.parentCtrl.getWidgetClass(widgetClass);

        if (vm.isWidgetEmpty()) {
            widgetClass.push('widget--is-empty');
        }

        if (angular.isDefined(_chartType)) {
            widgetClass.push(`widget-data--${_chartType}`);
        }

        return widgetClass;
    }

    /**
     * Is widget empty in designer mode.
     *
     * @return {boolean} If widget is hidden.
     */
    function isWidgetEmpty() {
        return (
            !Data.isCallInProgress(vm.parentCtrl.getListKey()) &&
            (angular.isUndefined(_chartType) || angular.isUndefinedOrEmpty(vm.widget.properties.url))
        );
    }

    /**
     * Is widget hidden in reading mode.
     *
     * @return {boolean} If widget is hidden.
     */
    function isWidgetHidden() {
        if (
            !vm.parentCtrl.designerMode() &&
            !Data.isCallInProgress(vm.parentCtrl.getListKey()) &&
            (angular.isUndefined(_chartType) || angular.isUndefinedOrEmpty(vm.widget.properties.url))
        ) {
            vm.parentCtrl.isHidden = true;
        } else {
            vm.parentCtrl.isHidden = false;
        }

        return vm.parentCtrl.isHidden;
    }

    /////////////////////////////

    vm.getWidgetClass = getWidgetClass;
    vm.isWidgetEmpty = isWidgetEmpty;
    vm.isWidgetHidden = isWidgetHidden;

    /////////////////////////////
    //                         //
    //          Events         //
    //                         //
    /////////////////////////////

    /**
     * When settings are updated, re-init.
     *
     * @param {Event}  evt        The event.
     * @param {string} widgetUuid Widget uuid.
     */
    $scope.$on('widget-data-settings', (evt, widgetUuid) => {
        if (vm.widget.uuid === widgetUuid) {
            vm.init();
        }
    });

    /////////////////////////////

    /**
     * Initialize controller.
     */
    vm.init = async function init() {
        let urlToFormatAndSave = angular.fastCopy(vm.widget.properties.url);
        _chartType = vm.widget.properties.type;

        if (angular.isDefined(_chartType) && angular.isDefinedAndFilled(vm.widget.properties.url)) {
            if (includes(urlToFormatAndSave, '?')) {
                urlToFormatAndSave = await _formatUrl(urlToFormatAndSave);
            }

            Data.get(
                {
                    raw: _chartType === 'raw',
                    url: urlToFormatAndSave,
                },
                (result) => {
                    if (result.raw) {
                        vm.rawContent = result.rawContent;
                    } else {
                        _chartContent = result.content;
                    }

                    $timeout(() => _initChart(_chartType));
                },
                Utils.displayServerError,
                vm.parentCtrl.getListKey(),
            );
        }
    };

    /**
     * Set parent controller.
     *
     * @param {Object} parentCtrl Parent Controller.
     */
    this.setParentController = function setParentController(parentCtrl) {
        vm.parentCtrl = parentCtrl;

        vm.init();
    };
}

function WidgetDataDirective() {
    'ngInject';

    function link(scope, el, attrs, ctrls) {
        ctrls[0].setParentController(ctrls[1]);
    }

    return {
        bindToController: true,
        controller: WidgetDataController,
        controllerAs: 'vm',
        link,
        replace: true,
        require: ['widgetData', '^widget'],
        restrict: 'E',
        scope: {
            widget: '=',
        },
        // eslint-disable-next-line max-len
        templateUrl: '/client/front-office/modules/content/modules/widget/modules/widget-data/views/widget-data.html',
    };
}

/////////////////////////////

angular.module('Widgets').directive('widgetData', WidgetDataDirective);

/////////////////////////////

export { WidgetDataController, WidgetDataDirective };
